import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Flex,
  HStack,
  PopoverBody,
  PopoverContent,
  PopoverPositioner,
  PopoverRoot,
  PopoverTrigger,
  Text,
  VStack,
  chakra,
} from "@chakra-ui/react";
import { Link, useBlocker, useNavigate } from "react-router-dom";
import { ModalPage } from "../../../components/ModalPage";
import dayjs from "dayjs";
import { useReq } from "@larner.dev/use-req";
import { ReqMethod } from "@larner.dev/req";
import imageCompression from "browser-image-compression";
import {
  DatingIntention,
  ProfilePictureKey,
  MAX_MEDIA_BYTES,
  MediaKey,
  UserWithMediaAndGroups,
  VALUE_MAX_LENGTH,
  datingIntentionMeta,
  getDatingIntentionValueCategoryDescription,
  getDatingIntentionValueCategoryName,
} from "@greenflagdate/shared";
import { Icon } from "../../../components/Icon/Icon";
import { GenderSelector } from "../../../components/GenderSelector/GenderSelector";
import { apiReq } from "../../../lib/apiReq";
import { getRuntimeApiUrl } from "../../../lib/runtimeApiUrl";
import { store, useStoreData } from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { toaster } from "../../../lib/toaster";
import { PictureEditor } from "./PictureEditor";
import { PictureData } from "../ProfileEditor/pictureMeta";
import headshotAvatar from "../ProfileEditor/images/headshot_avatar.png";
import bodyAvatar from "../ProfileEditor/images/body_avatar.png";
import actionAvatar from "../ProfileEditor/images/action_avatar.png";
import friendsAvatar from "../ProfileEditor/images/friends_avatar.png";
import travelAvatar from "../ProfileEditor/images/travel_avatar.png";
import petAvatar from "../ProfileEditor/images/pet_avatar.png";

const INTENTION_ORDER: DatingIntention[] = [
  DatingIntention.LifePartner,
  DatingIntention.LongTerm,
  DatingIntention.LongTermOpenToShortTerm,
  DatingIntention.ShortTerm,
  DatingIntention.ShortTermOpenToLongTerm,
  DatingIntention.FiguringOutDatingGoals,
];

const VALUE_PLACEHOLDERS: Record<DatingIntention, [string, string, string]> = {
  [DatingIntention.LifePartner]: [
    "e.g. Kindness",
    "e.g. Shared ambition",
    "e.g. Family first",
  ],
  [DatingIntention.LongTerm]: [
    "e.g. Kindness",
    "e.g. Shared ambition",
    "e.g. Family first",
  ],
  [DatingIntention.LongTermOpenToShortTerm]: [
    "e.g. Kindness",
    "e.g. Shared ambition",
    "e.g. Family first",
  ],
  [DatingIntention.ShortTerm]: [
    "e.g. Great conversations",
    "e.g. Clear boundaries",
    "e.g. Fun nights out",
  ],
  [DatingIntention.ShortTermOpenToLongTerm]: [
    "e.g. Great conversations",
    "e.g. Clear boundaries",
    "e.g. Fun nights out",
  ],
  [DatingIntention.FiguringOutDatingGoals]: [
    "Something that matters to you",
    "Something you want to explore",
    "A feeling you want to chase",
  ],
};

const PHOTO_TILES: {
  key: ProfilePictureKey;
  label: string;
  avatar: string;
}[] = [
  { key: ProfilePictureKey.Headshot, label: "Headshot", avatar: headshotAvatar },
  { key: ProfilePictureKey.Body, label: "Head to toe", avatar: bodyAvatar },
  { key: ProfilePictureKey.Action, label: "Hobby", avatar: actionAvatar },
  { key: ProfilePictureKey.Friends, label: "Friends", avatar: friendsAvatar },
  { key: ProfilePictureKey.Travel, label: "Location", avatar: travelAvatar },
  { key: ProfilePictureKey.Pet, label: "Pet / other", avatar: petAvatar },
];

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Text
      as="div"
      fontSize="10px"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="fg.muted"
      fontWeight="semibold"
      mb="10px"
    >
      {children}
    </Text>
  );
}

function LockedFieldPopover({ children }: { children: React.ReactNode }) {
  return (
    <PopoverRoot positioning={{ placement: "top" }}>
      <PopoverTrigger asChild>
        <Box>{children}</Box>
      </PopoverTrigger>
      <PopoverPositioner>
        <PopoverContent maxW="18rem" p="10px 12px">
          <PopoverBody p="0">
            <Text fontSize="13px" color="fg.default" lineHeight="1.4">
              <chakra.a
                as={Link}
                {...{ to: "/contact" }}
                color="fg.brand"
                textDecoration="underline"
              >
                Contact us
              </chakra.a>{" "}
              to change this.
            </Text>
          </PopoverBody>
        </PopoverContent>
      </PopoverPositioner>
    </PopoverRoot>
  );
}

function Card({
  children,
  flat,
}: {
  children: React.ReactNode;
  flat?: boolean;
}) {
  return (
    <Box
      bg="bg.canvas"
      border="1px solid"
      borderColor="border.subtle"
      borderRadius="sm"
      boxShadow={flat ? "none" : "card"}
      overflow="hidden"
    >
      {children}
    </Box>
  );
}

function FirstNameField({
  savedValue,
  draft,
  onChange,
  disabled,
}: {
  savedValue?: string;
  draft: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  if (savedValue) {
    return (
      <LockedFieldPopover>
        <Flex p="14px 16px" align="center" gap="3" cursor="pointer">
          <Box flex={1} minW={0}>
            <Text
              as="div"
              fontSize="10px"
              letterSpacing="0.1em"
              textTransform="uppercase"
              color="fg.muted"
              fontWeight="semibold"
              mb="1"
            >
              First Name
            </Text>
            <Text
              fontSize="16px"
              fontWeight="semibold"
              color="fg.default"
              lineHeight="1.2"
            >
              {savedValue}
            </Text>
          </Box>
          <Flex
            w="28px"
            h="28px"
            borderRadius="full"
            bg="bg.subtle"
            align="center"
            justify="center"
            color="fg.muted"
            flexShrink={0}
          >
            <Icon name="lock" boxSize="12px" />
          </Flex>
        </Flex>
      </LockedFieldPopover>
    );
  }

  return (
    <Box p="14px 16px">
      <Text
        as="div"
        fontSize="10px"
        letterSpacing="0.1em"
        textTransform="uppercase"
        color="fg.muted"
        fontWeight="semibold"
        mb="2"
      >
        First Name
      </Text>
      <Flex
        align="center"
        gap="2"
        p="11px 14px"
        border="1px solid"
        borderColor="border.default"
        borderRadius="xs"
        bg="bg.canvas"
        _focusWithin={{
          borderColor: "border.brand",
          boxShadow: "0 0 0 3px rgba(57,184,125,0.25)",
        }}
      >
        <chakra.input
          type="text"
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          placeholder="What should we call you?"
          disabled={disabled}
          flex={1}
          fontSize="14px"
          bg="transparent"
          border="0"
          outline="none"
          color="fg.default"
          _placeholder={{ color: "fg.muted", opacity: 0.65 }}
        />
      </Flex>
      <Text fontSize="11px" color="fg.muted" mt="6px" lineHeight="1.4">
        Your first name can't be changed after you save it.
      </Text>
    </Box>
  );
}

function BirthDateField({
  savedValue,
  draft,
  onChange,
  disabled,
}: {
  savedValue?: string;
  draft: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  if (savedValue) {
    const date = dayjs(savedValue);
    const age = dayjs().diff(date, "year");
    return (
      <LockedFieldPopover>
        <Flex p="14px 16px" align="center" gap="3" cursor="pointer">
          <Box flex={1} minW={0}>
            <Text
              as="div"
              fontSize="10px"
              letterSpacing="0.1em"
              textTransform="uppercase"
              color="fg.muted"
              fontWeight="semibold"
              mb="1"
            >
              Age
            </Text>
            <Text
              fontSize="16px"
              fontWeight="semibold"
              color="fg.default"
              lineHeight="1.2"
            >
              {age}
            </Text>
            <Text fontSize="12px" color="fg.muted" mt="0.5">
              Born {date.format("MMMM D, YYYY")}
            </Text>
          </Box>
          <Flex
            w="28px"
            h="28px"
            borderRadius="full"
            bg="bg.subtle"
            align="center"
            justify="center"
            color="fg.muted"
            flexShrink={0}
          >
            <Icon name="cake" boxSize="12px" />
          </Flex>
        </Flex>
      </LockedFieldPopover>
    );
  }

  return (
    <Box p="14px 16px">
      <Text
        as="div"
        fontSize="10px"
        letterSpacing="0.1em"
        textTransform="uppercase"
        color="fg.muted"
        fontWeight="semibold"
        mb="2"
      >
        Birth Date
      </Text>
      <Flex
        align="center"
        gap="2"
        p="11px 14px"
        border="1px solid"
        borderColor="border.default"
        borderRadius="xs"
        bg="bg.canvas"
        _focusWithin={{
          borderColor: "border.brand",
          boxShadow: "0 0 0 3px rgba(57,184,125,0.25)",
        }}
      >
        <chakra.input
          type="date"
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          max={dayjs().subtract(18, "year").format("YYYY-MM-DD")}
          disabled={disabled}
          flex={1}
          fontSize="14px"
          bg="transparent"
          border="0"
          outline="none"
          color="fg.default"
        />
      </Flex>
      <Text fontSize="11px" color="fg.muted" mt="6px" lineHeight="1.4">
        Your birth date can't be changed after you save it.
      </Text>
    </Box>
  );
}

function GenderField({ savedValue }: { savedValue?: string }) {
  const [genderReq, genderReqState] = useReq<UserWithMediaAndGroups>(apiReq);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<string | undefined>();

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setDraft(undefined);
  }, []);

  const save = useCallback(async () => {
    const gender = draft?.trim();
    if (!gender) return;
    const response = await genderReq.put("/user/attributes", { gender });
    if (response.success) {
      // The store update re-renders this field with the saved value, which
      // swaps it to the locked (read-only) state.
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result },
      });
      closeDialog();
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't save your gender. Please try again.",
      });
    }
  }, [genderReq, draft, closeDialog]);

  if (savedValue) {
    const label = savedValue.charAt(0).toUpperCase() + savedValue.slice(1);
    return (
      <LockedFieldPopover>
        <Flex p="14px 16px" align="center" gap="3" cursor="pointer">
          <Box flex={1} minW={0}>
            <Text
              as="div"
              fontSize="10px"
              letterSpacing="0.1em"
              textTransform="uppercase"
              color="fg.muted"
              fontWeight="semibold"
              mb="1"
            >
              Gender
            </Text>
            <Text
              fontSize="16px"
              fontWeight="semibold"
              color="fg.default"
              lineHeight="1.2"
            >
              {label}
            </Text>
          </Box>
          <Flex
            w="28px"
            h="28px"
            borderRadius="full"
            bg="bg.subtle"
            align="center"
            justify="center"
            color="fg.muted"
            flexShrink={0}
          >
            <Icon name="lock" boxSize="12px" />
          </Flex>
        </Flex>
      </LockedFieldPopover>
    );
  }

  return (
    <>
      <chakra.button
        type="button"
        onClick={() => setDialogOpen(true)}
        display="flex"
        alignItems="center"
        gap="3"
        w="100%"
        p="14px 16px"
        bg="transparent"
        border="0"
        cursor="pointer"
        textAlign="left"
        _hover={{ bg: "#FAFBFB" }}
      >
        <Box flex={1} minW={0}>
          <Text
            as="div"
            fontSize="10px"
            letterSpacing="0.1em"
            textTransform="uppercase"
            color="fg.muted"
            fontWeight="semibold"
            mb="1"
          >
            Gender
          </Text>
          <Text fontSize="14px" color="fg.muted" lineHeight="1.2">
            Tap to set your gender
          </Text>
        </Box>
        <Icon name="chevron-right" boxSize="14px" color="fg.muted" />
      </chakra.button>

      <DialogRoot
        open={dialogOpen}
        onOpenChange={(details) => {
          if (!details.open) closeDialog();
        }}
      >
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>What's your gender?</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <GenderSelector value={draft} onChange={setDraft} />
              <Text fontSize="11px" color="fg.muted" mt="14px" lineHeight="1.4">
                Your gender can't be changed after you save it.
              </Text>
            </DialogBody>
            <DialogFooter>
              <HStack gap="2" w="100%" justify="flex-end">
                <chakra.button
                  type="button"
                  onClick={closeDialog}
                  bg="transparent"
                  color="fg.muted"
                  border="1px solid"
                  borderColor="border.default"
                  borderRadius="pill"
                  h="40px"
                  px="4"
                  fontWeight="semibold"
                  fontSize="13px"
                  cursor="pointer"
                  _hover={{ bg: "bg.subtle" }}
                >
                  Cancel
                </chakra.button>
                <chakra.button
                  type="button"
                  onClick={() => void save()}
                  disabled={!draft?.trim() || genderReqState.loading}
                  bg={!draft?.trim() ? "bg.subtle" : "bg.brand"}
                  color={!draft?.trim() ? "fg.muted" : "fg.onBrand"}
                  border="0"
                  borderRadius="pill"
                  h="40px"
                  px="5"
                  fontWeight="bold"
                  fontSize="13px"
                  cursor={
                    !draft?.trim() || genderReqState.loading
                      ? "not-allowed"
                      : "pointer"
                  }
                  _hover={
                    draft?.trim() && !genderReqState.loading
                      ? { filter: "brightness(1.04)" }
                      : undefined
                  }
                >
                  {genderReqState.loading ? "Saving…" : "Save"}
                </chakra.button>
              </HStack>
            </DialogFooter>
            <DialogCloseTrigger />
          </DialogContent>
        </DialogPositioner>
      </DialogRoot>
    </>
  );
}

function IntentionPicker({
  value,
  onSelect,
  disabled,
}: {
  value?: DatingIntention;
  onSelect: (intention: DatingIntention) => void;
  disabled?: boolean;
}) {
  return (
    <VStack align="stretch" gap="2">
      {INTENTION_ORDER.map((intention) => {
        const on = value === intention;
        return (
          <chakra.button
            key={intention}
            type="button"
            onClick={() => !disabled && onSelect(intention)}
            // The onClick guard alone leaves the control focusable and
            // announced as actionable — until now nothing passed `disabled`,
            // so the gap was dormant.
            disabled={disabled}
            display="flex"
            alignItems="center"
            gap="3"
            w="100%"
            p="13px 14px"
            border="1px solid"
            borderColor={on ? "border.brand" : "border.default"}
            bg={on ? "bg.brandSoft" : "bg.canvas"}
            borderRadius="pill"
            cursor={disabled ? "not-allowed" : "pointer"}
            textAlign="left"
            transition="all 120ms ease"
            _hover={!disabled && !on ? { bg: "#FAFBFB" } : undefined}
            opacity={disabled ? 0.7 : 1}
          >
            <Flex
              w="20px"
              h="20px"
              borderRadius="full"
              border="1.5px solid"
              borderColor={on ? "border.brand" : "border.default"}
              bg={on ? "bg.brand" : "transparent"}
              align="center"
              justify="center"
              flexShrink={0}
            >
              {on && <Icon name="check" boxSize="10px" color="fg.onBrand" />}
            </Flex>
            <Text
              as="span"
              fontSize="13px"
              fontWeight={on ? "semibold" : "medium"}
              color={on ? "fg.onTint" : "fg.default"}
            >
              {datingIntentionMeta[intention].label}
            </Text>
          </chakra.button>
        );
      })}
    </VStack>
  );
}

function ValueInput({
  value,
  placeholder,
  onSave,
  disabled,
}: {
  value?: string;
  placeholder: string;
  onSave: (v: string) => Promise<void>;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed === (value ?? "")) return;
    setSaving(true);
    try {
      await onSave(trimmed);
    } finally {
      setSaving(false);
    }
  };

  const filled = draft.length > 0;
  return (
    <Flex
      align="center"
      gap="2"
      p="12px 14px"
      border="1px solid"
      borderColor="border.default"
      borderRadius="xs"
      bg="bg.canvas"
      _focusWithin={{
        borderColor: "border.brand",
        boxShadow: "0 0 0 3px rgba(57,184,125,0.25)",
      }}
    >
      <Icon
        name={filled ? "heart-filled" : "heart"}
        boxSize="14px"
        color={filled ? "fg.brand" : "border.default"}
      />
      <chakra.input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder={placeholder}
        maxLength={VALUE_MAX_LENGTH}
        disabled={saving || disabled}
        flex={1}
        fontSize="14px"
        bg="transparent"
        border="0"
        outline="none"
        color="fg.default"
        fontWeight={filled ? "medium" : "normal"}
        _placeholder={{ color: "fg.muted", opacity: 0.55 }}
      />
    </Flex>
  );
}

function CroppedImage({
  src,
  cropArea,
  alt,
}: {
  src: string;
  cropArea: { x: number; y: number; width: number; height: number };
  alt: string;
}) {
  const scale = 100 / cropArea.width;
  return (
    <Box position="absolute" inset={0} overflow="hidden">
      <chakra.img
        src={src}
        alt={alt}
        position="absolute"
        top={0}
        left={0}
        display="block"
        w="calc(100% + 0.5px)"
        h="auto"
        style={{
          transformOrigin: "top left",
          transform: `translate3d(${-cropArea.x * scale}%, ${-cropArea.y * scale}%, 0) scale3d(${scale}, ${scale}, 1)`,
        }}
      />
    </Box>
  );
}

function PhotoTile({
  label,
  picture,
  avatarSrc,
  primary,
  onClick,
}: {
  label: string;
  picture?: PictureData;
  avatarSrc: string;
  primary?: boolean;
  onClick: () => void;
}) {
  const empty = !picture;
  return (
    <Flex direction="column" gap="6px">
      <chakra.button
        type="button"
        onClick={onClick}
        position="relative"
        aspectRatio="1 / 1"
        borderRadius="xs"
        overflow="hidden"
        bg={empty ? "bg.subtle" : "#eee"}
        border={empty ? "1.5px dashed" : "0"}
        borderColor="border.default"
        boxShadow={empty ? "none" : "0 2px 2px rgba(0,0,0,0.10)"}
        cursor="pointer"
        p="0"
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        _hover={{ filter: "brightness(1.02)" }}
      >
        {empty ? (
          <Flex
            w="32px"
            h="32px"
            borderRadius="full"
            bg="bg.brandSoft"
            color="fg.brand"
            align="center"
            justify="center"
          >
            <Icon name="plus" boxSize="12px" />
          </Flex>
        ) : (
          <>
            <CroppedImage
              src={picture.imageSrc}
              cropArea={picture.cropArea}
              alt={label}
            />
            {primary && (
              <Box
                position="absolute"
                top="6px"
                left="6px"
                bg="bg.brand"
                color="fg.onBrand"
                fontSize="8px"
                fontWeight="bold"
                letterSpacing="0.08em"
                textTransform="uppercase"
                px="7px"
                py="3px"
                borderRadius="pill"
              >
                Main
              </Box>
            )}
            <Flex
              position="absolute"
              bottom="6px"
              right="6px"
              w="22px"
              h="22px"
              borderRadius="full"
              bg="rgba(0,0,0,0.55)"
              color="white"
              align="center"
              justify="center"
            >
              <Icon name="camera" boxSize="10px" />
            </Flex>
          </>
        )}
        {empty && (
          <Box
            position="absolute"
            inset={0}
            opacity={0.25}
            backgroundImage={`url(${avatarSrc})`}
            backgroundSize="cover"
            backgroundPosition="center"
            pointerEvents="none"
            mixBlendMode="multiply"
          />
        )}
      </chakra.button>
      <Text
        fontSize="11px"
        fontWeight="semibold"
        color={empty ? "fg.muted" : "fg.default"}
        textAlign="center"
      >
        {label}
      </Text>
    </Flex>
  );
}

export function Profile() {
  const navigate = useNavigate();
  const storeData = useStoreData();
  const user = storeData.user;

  const [basicsReq, basicsReqState] = useReq<UserWithMediaAndGroups>(apiReq);
  const [intentionReq] = useReq<UserWithMediaAndGroups>(apiReq);
  const [valuesReq] = useReq<UserWithMediaAndGroups>(apiReq);
  const [mediaReq] = useReq<UserWithMediaAndGroups>(apiReq);

  const [firstNameDraft, setFirstNameDraft] = useState("");
  const [birthDateDraft, setBirthDateDraft] = useState("");

  const hasPendingFirstName = !user?.first_name && firstNameDraft.trim().length > 0;
  const hasPendingBirthDate = !user?.birth_date && birthDateDraft.length > 0;
  const isDirty = hasPendingFirstName || hasPendingBirthDate;

  const [saving, setSaving] = useState(false);

  const datingIntention = user?.dating_intention as DatingIntention | undefined;
  const valueCategory = datingIntention
    ? datingIntentionMeta[datingIntention].valueCategory
    : undefined;
  const currentValues = useMemo(() => {
    if (!valueCategory) return ["", "", ""] as [string, string, string];
    const arr = user?.values?.[valueCategory] ?? [];
    return [arr[0] ?? "", arr[1] ?? "", arr[2] ?? ""] as [
      string,
      string,
      string,
    ];
  }, [user?.values, valueCategory]);

  const [activePictureType, setActivePictureType] = useState<
    ProfilePictureKey | undefined
  >();
  const [pictures, setPictures] = useState<
    Partial<Record<ProfilePictureKey, PictureData>>
  >({});

  useEffect(() => {
    const media = user?.media;
    if (!media) return;
    const keys = Object.keys(media);
    if (!keys.length) return;
    let cancelled = false;
    (async () => {
      const blobs = await Promise.all(
        keys.map(async (key) => {
          const entry = media[key as unknown as MediaKey];
          if (!entry) return null;
          const url = new URL(entry.urls.original, getRuntimeApiUrl());
          try {
            const res = await fetch(url, {
              headers: { Authorization: `Bearer ${storeData.auth.token}` },
            });
            if (!res.ok) return null;
            const blob = await res.blob();
            return {
              key,
              data: {
                blob,
                imageSrc: URL.createObjectURL(blob),
                cropArea: {
                  width: entry.crop_width_percent,
                  height: entry.crop_height_percent,
                  x: entry.crop_x,
                  y: entry.crop_y,
                },
              } as PictureData,
            };
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      const next: Partial<Record<ProfilePictureKey, PictureData>> = {};
      for (const entry of blobs) {
        if (entry) {
          next[entry.key as ProfilePictureKey] = entry.data;
        }
      }
      setPictures((prev) => ({ ...next, ...prev }));
    })();
    return () => {
      cancelled = true;
    };
  }, [storeData.auth.token, user?.media]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!isDirty) return true;

    const payload: { first_name?: string; birth_date?: string } = {};

    if (hasPendingFirstName) {
      payload.first_name = firstNameDraft.trim();
    }

    if (hasPendingBirthDate) {
      const parsed = dayjs(birthDateDraft);
      if (!parsed.isValid()) {
        toaster.create({
          type: "error",
          title: "That birth date doesn't look right.",
        });
        return false;
      }
      const age = dayjs().diff(parsed, "year");
      if (age < 18) {
        toaster.create({
          type: "error",
          title: "You must be at least 18 to use Green Flag.",
        });
        return false;
      }
      payload.birth_date = parsed.format("YYYY-MM-DD");
    }

    setSaving(true);
    try {
      const response = await basicsReq.put("/user/attributes", payload);
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
        setFirstNameDraft("");
        setBirthDateDraft("");
        return true;
      }
      toaster.create({
        type: "error",
        title: "Couldn't save your changes. Please try again.",
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    basicsReq,
    birthDateDraft,
    firstNameDraft,
    hasPendingBirthDate,
    hasPendingFirstName,
    isDirty,
  ]);

  const saveIntention = useCallback(
    async (intention: DatingIntention) => {
      const response = await intentionReq.put("/user/attributes", {
        dating_intention: intention,
      });
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
      } else {
        toaster.create({
          type: "error",
          title: "Couldn't save your dating intention.",
        });
      }
    },
    [intentionReq]
  );

  const saveValue = useCallback(
    async (index: number, newValue: string) => {
      if (!valueCategory) return;
      const nextForCategory = [...currentValues];
      nextForCategory[index] = newValue;
      const values = { ...(user?.values ?? {}), [valueCategory]: nextForCategory };
      const response = await valuesReq.put("/user/attributes", { values });
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
      } else {
        toaster.create({
          type: "error",
          title: "Couldn't save that value.",
        });
      }
    },
    [currentValues, user?.values, valueCategory, valuesReq]
  );

  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const dialogOpen = blocker.state === "blocked";
  const closeDialog = useCallback(() => {
    if (blocker.state === "blocked") blocker.reset();
  }, [blocker]);

  const proceedWithoutSaving = useCallback(() => {
    setFirstNameDraft("");
    setBirthDateDraft("");
    if (blocker.state === "blocked") blocker.proceed();
  }, [blocker]);

  const saveAndProceed = useCallback(async () => {
    const ok = await handleSave();
    if (!ok) return;
    if (blocker.state === "blocked") blocker.proceed();
  }, [blocker, handleSave]);

  // Friends Only withholds datingIntention and values from every profile
  // payload the server sends, so what's edited below is invisible to everyone
  // — including the user's own /profile preview, which shows the friends note
  // in its place. Lock the sections rather than hide them: the answers are
  // kept, and "turning the mode off restores what you had" is the promise the
  // Dating Preferences page already makes about the settings it locks.
  const friendsOnly = user?.friends_only ?? false;

  const valuesHeading = datingIntention
    ? getDatingIntentionValueCategoryName(datingIntention)
    : undefined;
  const valuesCopy = datingIntention
    ? getDatingIntentionValueCategoryDescription(datingIntention)
    : undefined;
  const valuesPlaceholders = datingIntention
    ? VALUE_PLACEHOLDERS[datingIntention]
    : undefined;

  return (
    <ModalPage
      motionKey="settings-profile-modal"
      title="Edit Profile"
      fallback="/settings"
      bodyBg="bg.canvas"
    >
    <Flex direction="column" minH="100%" w="100%">
      <Box
        flex="1"
        w="100%"
        maxW="40rem"
        mx="auto"
        pt="18px"
        pb={isDirty ? "110px" : "2rem"}
      >
      <Box px="4" pt="4">
        <Eyebrow>The basics</Eyebrow>
        <Card
          flat={!!user?.first_name && !!user?.birth_date && !!user?.gender}
        >
          <FirstNameField
            savedValue={user?.first_name}
            draft={firstNameDraft}
            onChange={setFirstNameDraft}
            disabled={saving}
          />
          <Box h="1px" bg="border.subtle" />
          <BirthDateField
            savedValue={user?.birth_date}
            draft={birthDateDraft}
            onChange={setBirthDateDraft}
            disabled={saving}
          />
          <Box h="1px" bg="border.subtle" />
          <GenderField savedValue={user?.gender} />
        </Card>
      </Box>

      <Box px="4" pt="6">
        <Eyebrow>Photos</Eyebrow>
        <Text fontSize="12px" color="fg.muted" lineHeight="1.5" mb="14px">
          Please upload at least one photo so we can connect you with others.
          Your headshot is shown as your main photo.
        </Text>
        <Box
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          gap="10px"
        >
          {PHOTO_TILES.map((t, i) => {
            const pic = pictures[t.key];
            return (
              <PhotoTile
                key={t.key}
                label={t.label}
                picture={pic}
                avatarSrc={t.avatar}
                primary={i === 0 && !!pic}
                onClick={() => setActivePictureType(t.key)}
              />
            );
          })}
        </Box>
      </Box>

      <Box
        px="4"
        pt="6"
        opacity={friendsOnly ? 0.5 : 1}
        transition="opacity 150ms ease"
      >
        <Eyebrow>Dating intention</Eyebrow>
        {friendsOnly && (
          <Text fontSize="12px" color="fg.muted" lineHeight="1.5" mb="10px">
            Hidden while Friends Only is on — people see “Friends only — not
            looking to date” here instead. Your answers are kept, and come back
            if you turn the mode off in{" "}
            <Link to="/settings/dating" style={{ textDecoration: "underline" }}>
              Dating Preferences
            </Link>
            .
          </Text>
        )}
        <IntentionPicker
          value={datingIntention}
          onSelect={saveIntention}
          disabled={friendsOnly}
        />
      </Box>

      {datingIntention && valuesHeading && valuesCopy && valuesPlaceholders && (
        <Box
          px="4"
          pt="6"
          opacity={friendsOnly ? 0.5 : 1}
          transition="opacity 150ms ease"
        >
          <Text
            fontSize="16px"
            fontWeight="semibold"
            color="fg.default"
            mb="1"
          >
            {valuesHeading}
          </Text>
          <Text
            fontSize="12px"
            color="fg.muted"
            lineHeight="1.5"
            mb="14px"
          >
            {valuesCopy}
          </Text>
          <VStack align="stretch" gap="10px">
            {[0, 1, 2].map((i) => (
              <ValueInput
                key={`${valueCategory}-${i}`}
                value={currentValues[i]}
                placeholder={valuesPlaceholders[i]}
                onSave={(v) => saveValue(i, v)}
                disabled={friendsOnly}
              />
            ))}
          </VStack>
        </Box>
      )}

      <PictureEditor
        isShown={!!activePictureType}
        pictureType={activePictureType}
        blob={activePictureType && pictures[activePictureType]?.blob}
        cropArea={activePictureType && pictures[activePictureType]?.cropArea}
        onClose={() => setActivePictureType(undefined)}
        onSave={async (data) => {
          const key = activePictureType;
          if (!key) return;
          setPictures((prev) => ({ ...prev, [key]: data }));
          setActivePictureType(undefined);

          let uploadBlob = data.blob;
          if (uploadBlob.size > MAX_MEDIA_BYTES) {
            uploadBlob = await imageCompression(uploadBlob as File, {
              maxSizeMB: MAX_MEDIA_BYTES / 1024 / 1024,
              useWebWorker: true,
            });
          }
          const formData = new FormData();
          formData.append("key", key);
          formData.append("cropWidthPercentage", data.cropArea.width.toString());
          formData.append(
            "cropHeightPercentage",
            data.cropArea.height.toString()
          );
          formData.append("cropX", data.cropArea.x.toString());
          formData.append("cropY", data.cropArea.y.toString());
          formData.append("media", uploadBlob, key);
          const response = await mediaReq[ReqMethod.PUT]("/media", formData);
          if (response.success) {
            store.dispatch({
              type: ActionType.UpdateUser,
              params: { user: response.result },
            });
          } else {
            setPictures((prev) => {
              const copy = { ...prev };
              delete copy[key];
              return copy;
            });
            toaster.create({
              type: "error",
              title:
                "Something went wrong uploading your photo. Please try again.",
            });
          }
        }}
        onDelete={async () => {
          const key = activePictureType;
          if (!key) return;
          const response = await mediaReq[ReqMethod.DELETE](
            `/media/${key}`,
            {}
          );
          if (response.success) {
            store.dispatch({
              type: ActionType.UpdateUser,
              params: { user: response.result },
            });
            setPictures((prev) => {
              const copy = { ...prev };
              delete copy[key];
              return copy;
            });
            setActivePictureType(undefined);
          }
        }}
      />

      </Box>

      {isDirty && (
        <Box
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          px="4"
          py="12px"
          bg="bg.canvas"
          borderTop="1px solid"
          borderColor="border.subtle"
          boxShadow="0 -4px 16px rgba(0,0,0,0.04)"
          zIndex={40}
          pb="calc(12px + env(safe-area-inset-bottom))"
        >
          <HStack justify="center" gap="3" maxW="40rem" mx="auto">
            <chakra.button
              onClick={() => navigate("/settings")}
              disabled={saving}
              type="button"
              bg="transparent"
              color="fg.muted"
              border="1px solid"
              borderColor="border.default"
              borderRadius="pill"
              h="44px"
              px="5"
              fontWeight="semibold"
              fontSize="14px"
              cursor={saving ? "not-allowed" : "pointer"}
              _hover={!saving ? { bg: "bg.subtle" } : undefined}
            >
              Cancel
            </chakra.button>
            <chakra.button
              onClick={() => void handleSave()}
              disabled={saving}
              type="button"
              bg={saving ? "bg.subtle" : "bg.brand"}
              color={saving ? "fg.muted" : "fg.onBrand"}
              border="0"
              borderRadius="pill"
              h="44px"
              px="6"
              fontWeight="bold"
              fontSize="14px"
              cursor={saving ? "not-allowed" : "pointer"}
              flex="1"
              maxW="18rem"
              boxShadow={saving ? "none" : "0 4px 12px rgba(57,184,125,0.3)"}
              _hover={!saving ? { filter: "brightness(1.04)" } : undefined}
            >
              {saving ? "Saving…" : "Save Changes"}
            </chakra.button>
          </HStack>
        </Box>
      )}

      <DialogRoot
        open={dialogOpen}
        onOpenChange={(details) => {
          if (!details.open) closeDialog();
        }}
      >
        <DialogBackdrop />
        <DialogPositioner>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Unsaved changes</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Text color="fg.default">
                You have unsaved changes to your profile. Would you like to
                save them before leaving?
              </Text>
              {(hasPendingFirstName || hasPendingBirthDate) && (
                <Text fontSize="12px" color="fg.muted" mt="2">
                  Note: your first name and birth date can't be changed after
                  saving.
                </Text>
              )}
            </DialogBody>
            <DialogFooter>
              <HStack gap="2" w="100%" justify="flex-end" flexWrap="wrap">
                <chakra.button
                  type="button"
                  onClick={closeDialog}
                  bg="transparent"
                  color="fg.muted"
                  border="1px solid"
                  borderColor="border.default"
                  borderRadius="pill"
                  h="40px"
                  px="4"
                  fontWeight="semibold"
                  fontSize="13px"
                  cursor="pointer"
                  _hover={{ bg: "bg.subtle" }}
                >
                  Keep editing
                </chakra.button>
                <chakra.button
                  type="button"
                  onClick={proceedWithoutSaving}
                  bg="transparent"
                  color="fg.danger"
                  border="1px solid"
                  borderColor="fg.danger"
                  borderRadius="pill"
                  h="40px"
                  px="4"
                  fontWeight="semibold"
                  fontSize="13px"
                  cursor="pointer"
                  _hover={{ bg: "#FEF5F5" }}
                >
                  Leave anyway
                </chakra.button>
                <chakra.button
                  type="button"
                  onClick={() => void saveAndProceed()}
                  disabled={saving || basicsReqState.loading}
                  bg="bg.brand"
                  color="fg.onBrand"
                  border="0"
                  borderRadius="pill"
                  h="40px"
                  px="4"
                  fontWeight="bold"
                  fontSize="13px"
                  cursor={saving ? "not-allowed" : "pointer"}
                  _hover={!saving ? { filter: "brightness(1.04)" } : undefined}
                >
                  {saving ? "Saving…" : "Save & leave"}
                </chakra.button>
              </HStack>
            </DialogFooter>
            <DialogCloseTrigger />
          </DialogContent>
        </DialogPositioner>
      </DialogRoot>
    </Flex>
    </ModalPage>
  );
}
